// Sensitivity grid computation — PSD §7.1 (F7.1, F4.3). Engine layer (framework-free).
import { runValuation } from './runValuation';
/**
 * Compute a 2-D sensitivity grid over two variables, holding others constant.
 * Returns FMV per share at each (x, y) intersection.
 */
export function computeSensitivityGrid(base, xVar, xValues, yVar, yValues) {
    const cells = [];
    for (const y of yValues) {
        const row = [];
        for (const x of xValues) {
            const variant = applyOverrides(base, { [xVar]: x, [yVar]: y });
            try {
                const r = runValuation(variant);
                row.push({ x, y, fmvPerShare: r.headline.commonFmvPerShare });
            }
            catch {
                row.push({ x, y, fmvPerShare: null });
            }
        }
        cells.push(row);
    }
    return {
        xAxis: { label: xVar, values: xValues },
        yAxis: { label: yVar, values: yValues },
        cells,
        benchmarkDlom: [
            { model: 'fixed', pct: base.discounts.dlomPercentage },
            { model: 'chaffe', pct: dlomForModel(base, 'chaffe') },
            { model: 'finnerty', pct: dlomForModel(base, 'finnerty') },
            { model: 'longstaff', pct: dlomForModel(base, 'longstaff') },
        ],
    };
}
function applyOverrides(base, o) {
    return {
        ...base,
        assumptions: {
            ...base.assumptions,
            volatility: o.volatility ?? base.assumptions.volatility,
            timeToLiquidity: o.timeToLiquidity ?? base.assumptions.timeToLiquidity,
        },
        discounts: {
            ...base.discounts,
            dlomPercentage: o.dlomPercentage ?? base.discounts.dlomPercentage,
        },
        equityValue: {
            ...base.equityValue,
            directEquityValue: o.totalEquityValue ?? base.equityValue.directEquityValue,
        },
    };
}
function dlomForModel(base, model) {
    // Reuse applyDlom with the model swapped
    // Avoid circular import by computing inline
    const A = base.assumptions;
    if (model === 'chaffe' || model === 'bsPut') {
        // bsPutImpliedDlom approximation: ATM put
        const T = A.timeToLiquidity;
        const sigma = A.volatility;
        const r = A.riskFreeRate;
        const d1 = (Math.log(1) + (r + (sigma * sigma) / 2) * T) / (sigma * Math.sqrt(T));
        const d2 = d1 - sigma * Math.sqrt(T);
        const N = (x) => 0.5 * (1 + erf(x / Math.SQRT2));
        const put = Math.exp(-r * T) * (1 - N(d2)) - 1 * (1 - N(d1));
        return Math.min(0.6, Math.max(0, put));
    }
    if (model === 'finnerty')
        return Math.min(0.6, Math.max(0, A.volatility * Math.sqrt(A.timeToLiquidity) * 0.5));
    /* v8 ignore start */
    if (model === 'longstaff')
        return Math.min(0.6, Math.max(0, A.volatility * Math.sqrt(A.timeToLiquidity) * 0.7));
    return base.discounts.dlomPercentage;
}
/* v8 ignore stop */
function erf(x) {
    const t = 1 / (1 + 0.5 * Math.abs(x));
    const inner = t *
        (1.00002368 +
            t *
                (0.37409196 +
                    t *
                        (0.09678418 +
                            t *
                                (-0.18628806 +
                                    t *
                                        (0.27886807 +
                                            t *
                                                (-1.13520398 +
                                                    t * (1.48851587 + t * (-0.82215223 + t * 0.17087277))))))));
    const tau = t * Math.exp(-x * x - 1.26551223 + inner);
    return x >= 0 ? 1 - tau : tau - 1;
}
//# sourceMappingURL=sensitivity.js.map